import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Package, CheckCircle } from 'lucide-react';

export function SampleRequestPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state?.product;
  
  const [formData, setFormData] = useState({
    company: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    quantity: '5',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitted(true);
    setIsSubmitting(false);
    toast.success('Demande d\'échantillon envoyée avec succès !');
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-center px-6" data-testid="sample-request-success">
        <div className="max-w-md">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-4 font-['Outfit']">Demande Envoyée !</h1>
          <p className="text-muted-foreground mb-6">
            Votre demande d'échantillon a bien été enregistrée. Notre équipe vous contactera sous 24-48h pour confirmer l'envoi.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={() => navigate('/catalogue')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Continuer mes achats
            </Button>
            <Button onClick={() => navigate('/dashboard')}>
              Mon espace
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="sample-request-page">
      {/* Header */}
      <div className="mb-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <h1 className="text-3xl font-bold font-['Outfit'] mb-2">Commander un Échantillon</h1>
        <p className="text-muted-foreground">
          Testez nos produits NFC avant de passer commande en grande quantité.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Product Info */}
        {product && (
          <div className="lg:col-span-1">
            <div className="bg-slate-50 rounded-xl p-6 sticky top-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-[#DC2626]" />
                <span className="font-medium">Produit sélectionné</span>
              </div>
              <img 
                src={product.images?.[0]} 
                alt={product.name_fr}
                className="w-full aspect-square object-contain bg-white rounded-lg mb-4"
              />
              <h3 className="font-semibold mb-2">{product.name_fr}</h3>
              <p className="text-sm text-muted-foreground">{product.description_fr?.slice(0, 100)}...</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className={product ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Info */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="font-semibold mb-4">Informations entreprise</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="company">Nom de l'entreprise *</Label>
                  <Input 
                    id="company"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    data-testid="sample-company-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contactName">Nom du contact *</Label>
                  <Input 
                    id="contactName"
                    required
                    value={formData.contactName}
                    onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                    data-testid="sample-contact-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email professionnel *</Label>
                  <Input 
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="sample-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input 
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="font-semibold mb-4">Adresse de livraison</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="address">Adresse *</Label>
                  <Input 
                    id="address"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    data-testid="sample-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">Ville *</Label>
                  <Input 
                    id="city"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="postalCode">Code postal *</Label>
                  <Input 
                    id="postalCode"
                    required
                    value={formData.postalCode}
                    onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="country">Pays</Label>
                  <Input 
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Sample Details */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="font-semibold mb-4">Détails de la demande</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="quantity">Quantité d'échantillons souhaitée</Label>
                  <Input 
                    id="quantity"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Maximum 20 unités par demande</p>
                </div>
                <div>
                  <Label htmlFor="message">Message (optionnel)</Label>
                  <Textarea 
                    id="message"
                    rows={4}
                    placeholder="Précisez vos besoins spécifiques, le contexte de votre projet..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
              <p className="text-amber-800">
                <strong>Note :</strong> Les échantillons sont réservés aux professionnels. 
                Des frais de port peuvent s'appliquer selon la destination. 
                Notre équipe vous contactera pour confirmer votre demande.
              </p>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-[#1F2937] hover:bg-[#1E293B]"
                data-testid="sample-submit-btn"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SampleRequestPage;
